import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Chip,
  Divider,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DeleteOutline,
  Warning,
  SignalCellularConnectedNoInternet0Bar,
  PhonelinkErase,
  Close,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 500,
    maxWidth: 650,
    borderRadius: 12,
  },
  titleBar: {
    background: "linear-gradient(135deg, #ff9800 0%, #e65100 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  content: {
    padding: "16px 20px",
    maxHeight: 450,
    overflowY: "auto",
  },
  ghostItem: {
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#fff8e1",
    border: `1px solid ${theme.palette.type === "dark" ? "#5c4a00" : "#ffe082"}`,
    padding: "8px 14px",
  },
  chip: {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
  },
  emptyIcon: {
    fontSize: 60,
    color: "#4caf50",
    marginBottom: 12,
  },
  actions: {
    padding: "12px 20px",
    justifyContent: "space-between",
  },
  summary: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  summaryCard: {
    flex: 1,
    minWidth: 120,
    padding: "10px 14px",
    borderRadius: 8,
    textAlign: "center",
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const getGhostReason = (whatsApp) => {
  if (!whatsApp.number && whatsApp.status === "PENDING") {
    return { label: "Nunca conectou", color: "#e65100" };
  }
  if (!whatsApp.number && whatsApp.status === "DISCONNECTED") {
    return { label: "Sem número", color: "#d32f2f" };
  }
  if (whatsApp.status === "DISCONNECTED" && whatsApp.retries >= 5) {
    return { label: "Reconexão falhou", color: "#880e4f" };
  }
  if (whatsApp.status === "DISCONNECTED") {
    return { label: "Desconectada", color: "#f57c00" };
  }
  if (whatsApp.status === "PENDING") {
    return { label: "Pendente", color: "#ff9800" };
  }
  if (whatsApp.status === "qrcode") {
    return { label: "Aguardando QR", color: "#1976d2" };
  }
  return { label: whatsApp.status, color: "#999" };
};

const GhostConnectionsModal = ({ open, onClose, whatsApps }) => {
  const classes = useStyles();
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Filtrar conexões fantasmas: sem número, DISCONNECTED, PENDING, ou qrcode parado
  const ghostConnections = (whatsApps || []).filter((w) => {
    const isNoNumber = !w.number || w.number === "" || w.number === "-";
    const isDisconnected = w.status === "DISCONNECTED";
    const isPending = w.status === "PENDING";
    const isQrStuck = w.status === "qrcode";
    const isFailedRetries = w.retries >= 5;

    return (
      (isNoNumber && (isDisconnected || isPending || isQrStuck)) ||
      (isDisconnected && isFailedRetries)
    );
  });

  useEffect(() => {
    if (!open) {
      setSelected([]);
    }
  }, [open]);

  const handleToggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selected.length === ghostConnections.length) {
      setSelected([]);
    } else {
      setSelected(ghostConnections.map((g) => g.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;

    setDeleting(true);
    let deletedCount = 0;

    for (const id of selected) {
      try {
        await api.delete(`/whatsapp/${id}`);
        deletedCount++;
      } catch (err) {
        console.error(`Erro ao deletar conexão ${id}:`, err);
      }
    }

    setDeleting(false);
    setSelected([]);

    if (deletedCount > 0) {
      toastUtils.success(
        `${deletedCount} conexão(ões) fantasma removida(s) com sucesso.`,
        "Limpeza Concluída"
      );
    }

    if (deletedCount === ghostConnections.length) {
      onClose();
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd/MM/yy HH:mm");
    } catch {
      return "-";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="ghost-connections-dialog"
    >
      <div className={classes.titleBar}>
        <div className={classes.titleContent}>
          <Warning />
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: 16 }}>
            Conexões Fantasmas ({ghostConnections.length})
          </Typography>
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
          <Close fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.content}>
        {ghostConnections.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" style={{ color: "#4caf50", fontWeight: 600 }}>
              ✅ Nenhuma conexão fantasma
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Todas as suas conexões estão ativas ou foram removidas corretamente.
            </Typography>
          </div>
        ) : (
          <>
            <div className={classes.summary}>
              <div className={classes.summaryCard}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#e65100" }}>
                  {ghostConnections.length}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Fantasmas
                </Typography>
              </div>
              <div className={classes.summaryCard}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#d32f2f" }}>
                  {ghostConnections.filter((g) => !g.number || g.number === "" || g.number === "-").length}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Sem Número
                </Typography>
              </div>
              <div className={classes.summaryCard}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#1976d2" }}>
                  {selected.length}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Selecionadas
                </Typography>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              <Checkbox
                checked={selected.length === ghostConnections.length && ghostConnections.length > 0}
                indeterminate={selected.length > 0 && selected.length < ghostConnections.length}
                onChange={handleSelectAll}
                color="primary"
                size="small"
              />
              <Typography variant="body2" color="textSecondary">
                Selecionar todas
              </Typography>
            </div>

            <List disablePadding>
              {ghostConnections.map((ghost) => {
                const reason = getGhostReason(ghost);
                return (
                  <ListItem key={ghost.id} className={classes.ghostItem}>
                    <Checkbox
                      checked={selected.includes(ghost.id)}
                      onChange={() => handleToggle(ghost.id)}
                      color="primary"
                      size="small"
                      style={{ marginRight: 8 }}
                    />
                    <ListItemIcon style={{ minWidth: 36 }}>
                      <SignalCellularConnectedNoInternet0Bar
                        style={{ color: reason.color, fontSize: 22 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            {ghost.name}
                          </Typography>
                          <Chip
                            label={reason.label}
                            size="small"
                            className={classes.chip}
                            style={{ backgroundColor: reason.color, color: "#fff" }}
                          />
                          {ghost.channel && ghost.channel !== "whatsapp" && (
                            <Chip
                              label={ghost.channel}
                              size="small"
                              className={classes.chip}
                              variant="outlined"
                            />
                          )}
                        </div>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          Número: {ghost.number || "Nenhum"} • Atualizado: {formatDate(ghost.updatedAt)}
                          {ghost.retries > 0 && ` • Tentativas: ${ghost.retries}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </div>

      <DialogActions className={classes.actions}>
        <Button size="small" onClick={onClose} style={{ textTransform: "none" }}>
          Fechar
        </Button>
        {ghostConnections.length > 0 && (
          <Button
            size="small"
            variant="contained"
            style={{
              textTransform: "none",
              backgroundColor: selected.length > 0 ? "#d32f2f" : "#ccc",
              color: "#fff",
            }}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
            onClick={handleDeleteSelected}
            disabled={selected.length === 0 || deleting}
          >
            {deleting
              ? "Removendo..."
              : `Remover ${selected.length > 0 ? `(${selected.length})` : ""}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GhostConnectionsModal;
